import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { pickAirportBg } from "../../lib/assets";

/* Home "Featured Destinations" strip — horizontal scroll of curated
   destination cards. Uses the destination's own hero image when set;
   otherwise auto-fetches a matching photo via `/api/unsplash/search`, and
   falls back to `pickAirportBg(name)` only when Unsplash returns nothing.
   Silent no-op when the API returns nothing so Home stays clean on fresh
   installs. */

function useUnsplashFallback(query, enabled) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!enabled || !query) { setUrl(null); return undefined; }
    let ignore = false;
    api.get("/unsplash/search", { params: { q: query, per_page: 1 } })
      .then((r) => {
        if (ignore) return;
        const first = (r.data?.results || r.data?.items || [])[0];
        const src = first?.url_regular || first?.urls?.regular || first?.image_url || first?.image || first?.url;
        if (src) setUrl(src);
      })
      .catch(() => { /* silent */ });
    return () => { ignore = true; };
  }, [query, enabled]);
  return url;
}

function DestinationTile({ d }) {
  const hasImage = Boolean(d.hero_image || d.cover_image);
  const query = hasImage ? null : `${d.name} ${d.country || ""}`.trim();
  const auto = useUnsplashFallback(query, !hasImage);
  const img = d.hero_image || d.cover_image || auto || pickAirportBg(d.id || d.name);
  return (
    <Link to={`/destinations/${d.id}`}
      data-testid={`home-featured-destination-${d.id}`}
      className="group relative shrink-0 snap-start w-[220px] sm:w-[260px] aspect-[3/4] rounded-2xl overflow-hidden border border-border bg-card hover:border-accent hover:shadow-warm-md transition-[border-color,box-shadow] tap">
      <img src={img} alt={d.name} loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
        <div className="font-serif font-800 text-lg leading-tight drop-shadow-sm">{d.name}</div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-white/85 mt-0.5 flex items-center gap-1">
          <MapPin className="w-3 h-3" strokeWidth={2} /> {d.country || d.region || "—"}
        </div>
        {d.tagline && <div className="text-xs text-white/80 mt-1.5 line-clamp-2 leading-snug">{d.tagline}</div>}
      </div>
    </Link>
  );
}

export default function HomeFeaturedDestinations({ limit = 8 }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/destinations", { params: { limit, featured: 1 } })
      .then((r) => setItems((r.data.items || []).slice(0, limit)))
      .catch(() => setItems([]));
  }, [limit]);

  if (items === null || items.length === 0) return null;

  return (
    <section data-testid="home-featured-destinations">
      <div className="flex items-end justify-between mb-3 gap-3 px-1">
        <div>
          <h2 className="font-serif font-800 text-2xl text-primary leading-tight">Featured destinations</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Curated by our community — swipe to explore.</p>
        </div>
        <Link to="/destinations" data-testid="home-featured-destinations-more"
          className="shrink-0 inline-flex items-center gap-1 text-xs font-700 text-accent hover:underline whitespace-nowrap">
          See all <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <div className="overflow-x-auto -mx-4 sm:-mx-6 px-4 sm:px-6 scroll-smooth snap-x snap-mandatory">
        <div className="flex gap-3 pb-2">
          {items.map((d) => <DestinationTile key={d.id} d={d} />)}
        </div>
      </div>
    </section>
  );
}

